import { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Swords, Calendar, Users, UserSearch, Trophy, Bomb, Settings, Gavel, Zap, ArrowLeftRight, ChevronDown, MoreHorizontal, DollarSign } from 'lucide-react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { supabase } from '@/integrations/supabase/client';
import sabotageLogoUrl from '@/assets/sabotage-logo.png';
import MobileHamburgerMenu from '@/components/MobileHamburgerMenu';
import UserAccountMenu from '@/components/UserAccountMenu';
import NotificationBell from '@/components/NotificationBell';

interface NFLNavProps {
  activeTab: string;
  onTabChange: (tab: string) => void;

  /**
   * SAB-871: hide the mobile bottom bar, because something else is providing one.
   *
   * OPTIONAL AND DEFAULTING TO FALSE ON PURPOSE. The two other call sites -- the non-draft
   * shell branch and WalletShell -- are then unchanged BY CONSTRUCTION rather than by
   * remembering not to pass it.
   *
   * The caller must key this on WHICH ROOM is rendering, never on `activeTab === 'draft'`.
   * The draft tab also serves the LEGACY room, which has no tab bar of its own; suppressing
   * the app nav there leaves a mobile user with zero navigation.
   */
  hideBottomNav?: boolean;
}

// SAB-512 Phase 2: desktop tab strip. Order + per-tab emoji match MLBBetaNav
// (emoji-in-label, no Lucide on desktop). Trades folded into My Team; Settings
// moved to the right-cluster gear. Mobile bottom bar (mobileBottomItems) stays Lucide.
const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'team', label: '🏟️ My Team' },
  { id: 'matchups', label: '⚔️ Matchups' },
  { id: 'standings', label: '🏆 Standings' },
  { id: 'schedule', label: '📅 Schedule' },
  { id: 'draft', label: '📝 Draft' },
  { id: 'players', label: '👤 Players' },
  { id: 'sabotages', label: '💣 Sabotages' },
];

// SAB-512 Phase 3: mobile bottom bar. 5 Lucide items incl. Home (Adam's lineup;
// keeps Sabotages, diverging from MLB's Players slot). "More" opens a bottom sheet.
const mobileBottomItems = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'team', label: 'My Team', icon: Users },
  { id: 'matchups', label: 'Matchups', icon: Swords },
  { id: 'sabotages', label: 'Sabotages', icon: Bomb },
];

// "More" bottom-sheet destinations (2-col grid). Wallet deep-links into the
// Settings funds section via the NFLStaging 'wallet' pseudo-route.
const moreSheetItems = [
  { id: 'standings', label: 'Standings', icon: Trophy },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'players', label: 'Players', icon: UserSearch },
  { id: 'draft', label: 'Draft', icon: Gavel },
  { id: 'wallet', label: 'Wallet', icon: DollarSign },
  { id: 'settings', label: 'Settings', icon: Settings },
];

/* ------------------------------------------------------------------ */
/*  League Switcher Dropdown                                            */
/* ------------------------------------------------------------------ */
function LeagueSwitcher({ onTabChange }: { onTabChange: (tab: string) => void }) {
  const { leagues, currentLeague, setCurrentLeagueId } = useNFLLeague();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (leagues.length === 0) {
    return (
      <button
        onClick={() => navigate('/nfl/create')}
        className="text-xs text-primary hover:bg-primary/10 font-medium transition-colors px-2.5 py-1 rounded-full border border-primary/40"
      >
        Join or Create
      </button>
    );
  }

  // SAB-592: one league or many, always expose Join + Create. The old code showed a
  // bare name for 1 league and a switch-only dropdown for 2+, so a user who already
  // belonged to a league had NO way to join or create another.
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs font-medium text-foreground hover:text-primary transition-colors max-w-[140px]"
      >
        <span className="truncate min-w-0">{currentLeague?.name || 'Select'}</span>
        <ChevronDown className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 min-w-[180px] rounded-lg border border-border bg-popover shadow-lg py-1">
            {leagues.map(l => (
              <button
                key={l.id}
                onClick={() => { setCurrentLeagueId(l.id); setOpen(false); }}
                className={cn(
                  'w-full px-3 py-2 text-xs text-left hover:bg-muted transition-colors',
                  l.id === currentLeague?.id && 'bg-primary/5 text-primary'
                )}
              >
                {/* SAB-575: league name only — the status pill was wrapping ("Pre-"/
                    "Draft") and squishing the name. Name gets the full row width. */}
                <span className="block truncate">{l.name}</span>
              </button>
            ))}
            <div className="my-1 h-px bg-border" />
            <button
              onClick={() => { onTabChange('join'); setOpen(false); }}
              className="w-full px-3 py-2 text-xs text-left font-medium text-primary hover:bg-muted transition-colors"
            >
              + Join a league
            </button>
            <button
              onClick={() => { onTabChange('create'); setOpen(false); }}
              className="w-full px-3 py-2 text-xs text-left font-medium text-primary hover:bg-muted transition-colors"
            >
              + Create a league
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Top Bar — logo row + horizontal tab strip                          */
/* ------------------------------------------------------------------ */
function FootballTopBar({ activeTab, onTabChange }: NFLNavProps) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { currentLeague } = useNFLLeague();
  const [credits, setCredits] = useState<number | null>(null);

  // Auto-scroll the active tab into view. Wrapped in rAF + useLayoutEffect
  // so it runs after layout but before paint — fixes a bug where on initial
  // mount the active tab (e.g. "Draft", which is the 4th tab) would render
  // off-screen on 360-390px viewports because layout wasn't settled when the
  // scroll fired.
  const isFirstScrollRef = useRef(true);
  useLayoutEffect(() => {
    if (!tabsRef.current) return;
    const id = requestAnimationFrame(() => {
      const active = tabsRef.current?.querySelector('[data-active="true"]');
      if (active) {
        active.scrollIntoView({
          behavior: isFirstScrollRef.current ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
        isFirstScrollRef.current = false;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [activeTab]);

  // Fetch sabotage credits
  useEffect(() => {
    if (!user || !currentLeague) return;
    supabase
      .from('player_wallets')
      .select('sabotage_credits')
      .eq('user_id', user.id)
      .eq('league_id', currentLeague.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setCredits(data.sabotage_credits);
      });
  }, [user, currentLeague, activeTab]);

  return (
    <div
      className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* SAB-398 originally viewport-centered the switcher via
          minmax(0,1fr)_auto_minmax(0,1fr). SAB-569: NFL's right cluster carries a
          credits pill (MLB hides its pill on mobile — MLBBeta.tsx:287), making the
          cluster wide enough that a viewport-centered `auto` league name overlapped
          it. Fixed by giving the league name the flexible middle track
          (auto_minmax(0,1fr)_auto): logo + icons keep their natural widths, the name
          centers in the LEFTOVER space and truncates, so it can never collide with
          the cluster. Slight off-viewport-center is the deliberate trade for no
          overlap. sm+ still falls back to flex. */}
      <div className="container mx-auto px-3 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center h-14 max-w-6xl gap-3 sm:flex">
        <div className="flex items-center shrink-0 justify-self-start">
          {/* SAB-512 Phase 4: de-boxed logo only - no wordmark, no sport emoji (matches MLB). */}
          <Link to="/" className="flex items-center cursor-pointer">
            <img src={sabotageLogoUrl} alt="Sabotage Fantasy" className="h-8 w-auto" />
          </Link>
          {/* SAB-575: desktop same-sport league switcher, left-of-brand (Sleeper/ESPN
              pattern). Below lg the centered switcher takes over. Same LeagueSwitcher
              component, so both breakpoints share one source of truth. */}
          <div className="hidden lg:flex items-center min-w-0 ml-3">
            <LeagueSwitcher onTabChange={onTabChange} />
          </div>
        </div>

        {/* League switcher: mobile/tablet center; hidden on desktop where the nav
            takes the center (matches MLBBeta.tsx:234, flex lg:hidden). */}
        <div className="flex lg:hidden items-center justify-center min-w-0 justify-self-center">
          <LeagueSwitcher onTabChange={onTabChange} />
        </div>

        {/* SAB-512 Phase 4: nav tabs inline in the single header row (no separate
            strip below). Tablet+; horizontally scrollable if tight. */}
        <div
          ref={tabsRef}
          className="hidden sm:flex flex-1 items-center justify-center overflow-x-auto no-scrollbar gap-1"
        >
          {navItems.map(item => (
            <button
              key={item.id}
              data-active={activeTab === item.id}
              onClick={() => onTabChange(item.id)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all shrink-0',
                activeTab === item.id
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 shrink-0 justify-self-end">
          {/* SAB-512 Phase 2: Settings gear (desktop) - Settings moved out of the
              tab strip into the right cluster, matching MLB. Mobile reaches
              Settings via the hamburger drawer, so this is gated hidden sm:flex. */}
          <button
            onClick={() => onTabChange('settings')}
            aria-label="Settings"
            data-active={activeTab === 'settings'}
            className={cn(
              'hidden sm:flex items-center justify-center h-8 w-8 rounded-full transition-colors',
              activeTab === 'settings'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
          >
            <Settings className="h-4 w-4" />
          </button>

          {/* SAB-397: avatar dropdown (account settings + sign out). Shows
              on all viewports — not breakpoint-gated, matching MLB. */}
          <UserAccountMenu />
          {/* SAB-569: credits pill lives at the right edge next to notifications
              (was the cluster's leftmost item, jammed against the centered league
              name). Sits by the bell so it reads as an account/status chip, not
              part of the league title. */}
          {credits !== null && (
            <button
              onClick={() => onTabChange('settings')}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted border border-border text-muted-foreground text-xs font-medium hover:bg-muted/80 transition-colors shrink-0"
            >
              <Zap className="h-3 w-3" />
              {credits}
            </button>
          )}
          {/* SAB-395: mirror MLB's icons cluster. NotificationBell self-gates
              with lg:hidden (mobile/tablet only); desktop inline bell is a
              separate concern handled by the nav strip when it's added. */}
          <NotificationBell />
          {/* SAB-958: the hamburger is now the RIGHT-MOST control, with the bell and the
              balance pill to its left. It used to sit before them, which put the menu in the
              middle of the cluster; on a phone the right edge is where a thumb reaches for a
              menu. Moving the hamburger rather than reordering the other three keeps their
              established relative order (SAB-395/397/569) untouched.

              SAB-396: shared MobileHamburgerMenu (drawer with user account, Beta Credits,
              app settings, Sport switcher to MLB, Sign Out). Replaces the deleted
              FootballHamburgerMenu — the shared one already covers what NFL needs and
              matches MLB's drawer 1:1. */}
          <MobileHamburgerMenu activeTab={activeTab} onTabChange={onTabChange} />
        </div>
      </div>

    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Bottom Nav — mobile quick access (matches MLB BottomNav)           */
/* ------------------------------------------------------------------ */
function FootballBottomNav({ activeTab, onTabChange }: NFLNavProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  // "More" is active when the current tab is one of its sheet destinations.
  const moreActive = moreOpen || moreSheetItems.some(i => i.id === activeTab);

  return (
    <div
      className="sm:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border"
      style={{ backgroundColor: 'hsl(222 47% 6%)' }}
    >
      <nav>
        <div className="grid grid-cols-5 items-center" style={{ height: '60px' }}>
          {mobileBottomItems.map(item => (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={cn(
                'flex flex-col items-center justify-center gap-1 h-full min-h-[44px] transition-all',
                activeTab === item.id
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <item.icon className={cn(
                'w-5 h-5 transition-transform',
                activeTab === item.id && 'scale-110'
              )} />
              <span className={cn(
                'text-[10px] font-medium',
                activeTab === item.id && 'font-semibold'
              )}>
                {item.label}
              </span>
            </button>
          ))}
          {/* More -> bottom sheet with the remaining destinations */}
          <button
            onClick={() => setMoreOpen(true)}
            aria-label="More"
            className={cn(
              'flex flex-col items-center justify-center gap-1 h-full min-h-[44px] transition-all',
              moreActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <MoreHorizontal className={cn('w-5 h-5 transition-transform', moreActive && 'scale-110')} />
            <span className={cn('text-[10px] font-medium', moreActive && 'font-semibold')}>More</span>
          </button>
        </div>
        <div style={{ height: 'max(env(safe-area-inset-bottom, 20px), 16px)' }} />
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen}>
        <DrawerContent>
          <div className="px-4 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+16px)]">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 px-1">More</p>
            <div className="grid grid-cols-2 gap-2">
              {moreSheetItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { onTabChange(item.id); setMoreOpen(false); }}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border p-3 min-h-[44px] text-sm font-medium transition-colors',
                    activeTab === item.id
                      ? 'bg-primary/10 border-primary/30 text-primary'
                      : 'bg-muted/40 border-border text-foreground hover:bg-muted'
                  )}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Composed export — top bar + bottom nav only, NO sidebar            */
/* ------------------------------------------------------------------ */
export default function NFLNav({ activeTab, onTabChange, hideBottomNav = false }: NFLNavProps) {
  return (
    <>
      {/* The TOP bar always stays. It carries the logo and the league switcher, which remain
          the secondary way out of a room that has hidden the bottom bar. */}
      <FootballTopBar activeTab={activeTab} onTabChange={onTabChange} />
      {!hideBottomNav && <FootballBottomNav activeTab={activeTab} onTabChange={onTabChange} />}
    </>
  );
}
